/**
 * @file Type definitions and configurations for cloud integration components
 * @description Contains all TypeScript types, enums, interfaces, and configuration objects
 * used throughout the integration card system for connecting cloud providers (AWS, Azure, GCP, M365)
 * with security modules (CSPM, CIEM).
 */

/**
 * Enum representing available integration security modules
 * @enum {string}
 * @property {string} CSPM - Cloud Security Posture Management: Monitors cloud infrastructure for security compliance
 * @property {string} CIEM - Cloud Infrastructure Entitlement Management: Manages identity and access risks
 */
export enum IntegrationModule {
  CSPM = 'cspm',
  CIEM = 'ciem',
}

/**
 * Enum representing supported cloud providers
 * @enum {string}
 * @property {string} AWS - Amazon Web Services
 * @property {string} AZURE - Microsoft Azure
 * @property {string} GCP - Google Cloud Platform
 * @property {string} M365 - Microsoft 365
 */
export enum CloudProvider {
  AWS = 'aws',
  AZURE = 'azure',
  GCP = 'gcp',
  M365 = 'm365',
}

/**
 * Enum representing integration scope levels
 * @enum {string}
 * @property {string} ACCOUNT_LEVEL - Single account integration (one AWS/Azure/GCP account)
 * @property {string} ORGANIZATION_LEVEL - Organization-wide integration (multiple accounts under one organization)
 */
export enum IntegrationType {
  ACCOUNT_LEVEL = 'ACCOUNT_LEVEL',
  ORGANIZATION_LEVEL = 'ORGANIZATION_LEVEL',
}

/**
 * Interface representing the complete integration data collected through the multi-step wizard
 * @interface IntegrationData
 * @property {CloudProvider} [cloudProvider] - Selected cloud provider (AWS, Azure, etc.)
 * @property {IntegrationModule} [integrationModule] - Selected security module (CSPM, CIEM)
 * @property {IntegrationType} [integrationType] - Integration scope (account or organization level)
 * @property {string} [integrationLabel] - User-friendly name for the integration
 * @property {string} [accountId] - Cloud account or organization ID
 * @property {string} [accessKey] - Cloud provider access key/client ID
 * @property {string} [secretKey] - Cloud provider secret key/client secret
 * @property {string} [cfsLink] - CloudFormation Stack link (returned from API after integration creation)
 * @property {string} [cloudTrailExpiration] - CloudTrail log expiration setting (specific to AWS)
 * @property {string} [cloudTrailNonCurrent] - CloudTrail non-current log expiration setting (specific to AWS)
 * @property {string} [athenaResultsExpiration] - Athena query results expiration setting (specific to AWS)
 */
export interface IntegrationData {
  cloudProvider?: CloudProvider;
  integrationModule?: IntegrationModule;
  integrationType?: IntegrationType;
  integrationLabel?: string;
  accountId?: string;
  accessKey?: string;
  secretKey?: string;
  cfsLink?: string;
  cloudTrailExpiration?: string;
  cloudTrailNonCurrent?: string;
  athenaResultsExpiration?: string;
}

/**
 * Props for the IntegrationCard component
 * @interface IntegrationCardProps
 * @property {CloudProvider} cloudProvider - The cloud provider to display
 * @property {IntegrationModule} integrationModule - The integration module to display
 * @property {Function} onClick - Callback function triggered when card is clicked
 */
export interface IntegrationCardProps {
  cloudProvider: CloudProvider;
  integrationModule: IntegrationModule;
  onClick: () => void;
}

/**
 * Configuration structure for cloud provider display information
 * @interface CloudProviderConfig
 * @property {string} label - Short label (e.g., "AWS")
 * @property {string} fullName - Full name (e.g., "Amazon Web Services")
 * @property {string} [icon] - Optional icon identifier
 */
export interface CloudProviderConfig {
  label: string;
  fullName: string;
  icon?: string;
}

/**
 * Configuration structure for integration module display information
 * @interface IntegrationModuleConfig
 * @property {string} label - Short label (e.g., "CSPM")
 * @property {string} fullName - Full name (e.g., "Cloud Security Posture Management")
 * @property {string} description - User-facing description shown in tooltips
 */
export interface IntegrationModuleConfig {
  label: string;
  fullName: string;
  description: string;
}

/**
 * Configuration mapping for all supported cloud providers
 * Maps each CloudProvider enum to its display configuration (labels and full names)
 * Used to display provider information consistently across the UI
 */
export const CLOUD_PROVIDER_CONFIG: Record<CloudProvider, CloudProviderConfig> =
  {
    [CloudProvider.AWS]: {
      label: 'AWS',
      fullName: 'Amazon Web Services',
    },
    [CloudProvider.AZURE]: {
      label: 'Azure',
      fullName: 'Microsoft Azure',
    },
    [CloudProvider.GCP]: {
      label: 'GCP',
      fullName: 'Google Cloud Platform',
    },
    [CloudProvider.M365]: {
      label: 'Microsoft 365',
      fullName: 'Microsoft 365',
    },
  };

export const INTEGRATION_MODULE_CONFIG: Record<
  IntegrationModule,
  IntegrationModuleConfig
> = {
  [IntegrationModule.CSPM]: {
    label: 'CSPM',
    fullName: 'Cloud Security Posture Management',
    description:
      'Connect your AWS accounts for security posture management and compliance monitoring.',
  },
  [IntegrationModule.CIEM]: {
    label: 'CIEM',
    fullName: 'Cloud Infrastructure Entitlement Management',
    description:
      'Connect your cloud environments to discover and manage identity-based security risks.',
  },
};
